import React from "react";
import {graphql, withApollo} from "react-apollo";
import { flowRight as compose } from "lodash";
import gql from "graphql-tag";
import {connect} from "react-redux";
import {formValueSelector, change} from "redux-form/immutable";
import Form from "./ScheduleShipmentForm";
import {SHIPMENT_SCHEDULE_WITH_RECOMMENDATION_AND_HOLIDAYS} from "../../queries/shipping";
import Loading from "../../components/Loading";
import GraphQLErrors from "../../components/GraphQLErrors";
import {cancelScheduleShipment} from "../../actions/ShippingApiActions";
import {showDatePicker} from "../../actions/ViewActions";
import {fromJS, Map} from "immutable";
import {validatePresence,} from "../../lib/Validators";
import MessageBus from "../../lib/MessageBus";
import format from "date-fns/format";
import {createValidationErrors, createErrors} from "../../lib/ErrorFormatter";

function validateForm(values) {
  let errors = Map();
  errors = validatePresence(errors, values, "prepDate");
  errors = validatePresence(errors, values, "pickupDate");
  return errors.toJS();
}

class ScheduleShipmentFormContainer extends React.Component {

  constructor(props) {
    super(props);
    this.state = { 
      errors: [],
      deliveryDate: "", // used when working with deliveryDate
      pickupDate: "", // used when working with pickupDate
      loadingDeliveryDate: false
    }
  }

  componentDidMount() {
    this.deliveryDateSubscription = MessageBus.subscribe("DatePicker", "deliveryDate", this.onDeliveryDateSelected.bind(this))
  }

  componentWillUnmount() {
    MessageBus.unsubscribe(this.deliveryDateSubscription);
  }

  onDeliveryDateSelected(date) {
    this.setState({loadingDeliveryDate: true})
    this.retrieveScheduleFromDeliveryDate(format(date, "YYYY-MM-DD"))
  }

  retrieveScheduleFromDeliveryDate(deliveryDate) {
    return this.props.client.query({
      query: SHIPMENT_DELIVERY_SCHEDULE,
      fetchPolicy: "network-only",
      variables: {
        orderId: this.props.orderId,
        deliveryDate: deliveryDate
      }
    })
    .then((response) => {
      if (response.data.shipmentDeliverySchedule) {
        this.props.change("ScheduleShipmentForm", "prepDate", response.data.shipmentDeliverySchedule.prepDate);
        this.props.change("ScheduleShipmentForm", "pickupDate", response.data.shipmentDeliverySchedule.pickupDate);        
        this.setState({
          pickupDate: response.data.shipmentDeliverySchedule.pickupDate,
          deliveryDate: response.data.shipmentDeliverySchedule.deliveryDate,
          loadingDeliveryDate: false
        })
      } else {
        this.setState({loadingDeliveryDate: false })
      }
    })
    .catch((err) => {
      console.warn(err);
      this.setState({loadingDeliveryDate: false })
    });
  }  

  handleSubmit(values) {
    let updatedValues = {
      orderId: this.props.orderId,
      prepDate: values.get("prepDate"),
      pickupDate: values.get("pickupDate")
    }
    return this.props.scheduleShipment(updatedValues)
      .then((response) => {
        if (response.data.scheduleShipment.errors.length <= 0) {
          this.props.cancelScheduleShipment();
        } else {
          let errors = createValidationErrors(response.data.scheduleShipment.errors);
          this.setState({errors: errors});
        }
      })
      .catch((err) => {
        this.setState({errors: createErrors(err)});
      });
  }

  handleAutoSetDates() {
    this.props.change("ScheduleShipmentForm", "prepDate", this.props.data.shipmentLoadingRecommendation.prepDate);
    this.props.change("ScheduleShipmentForm", "pickupDate", this.props.data.shipmentLoadingRecommendation.pickupDate);
  }

  handleUseCalendar() {
    let unavailableDates = fromJS(this.props.data.unavailableShippingDatesForOrder).reduce((acc, x) => {
      return acc.set(x.get("date"), x.get("event"));
    }, Map()).toJS();
    this.props.showDatePicker("deliveryDate", this.props.data.shipmentLoadingRecommendation.pickupDate, { disableWeekends: true, unavailableDates: unavailableDates, notBeforeDate: this.props.data.shipmentLoadingRecommendation.pickupDate});
  }

  handleCancel() {
    this.props.cancelScheduleShipment();
  }

  handleShowDatePicker(field, date, options) {
    let unavailableDates = fromJS(this.props.data.unavailableShippingDatesForOrder).reduce((acc, x) => {
      return acc.set(x.get("date"), x.get("event"));
    }, Map()).toJS();
    this.props.showDatePicker(field, date, { disableWeekends: true, unavailableDates: unavailableDates });
  }

  render() {
    let {data} = this.props;
    if (data.loading) return <Loading />
    if (data.error) return <GraphQLErrors error={data.error} />
    let recommended = data.shipmentLoadingRecommendation;
    let schedule = data.shipmentLoadingSchedule;
    let initialValues = fromJS({
      prepDate: recommended.prepDate,
      pickupDate: recommended.pickupDate
    })
    let showDatesAreRecommended = 
      this.props.selectedPrepDate === recommended.prepDate &&
      this.props.selectedPickupDate === recommended.pickupDate;
    let message = "";
    if (schedule.scheduled) {
      message = "This shipment has already been scheduled. Submitting new dates will reschedule the shipment.";
      initialValues = fromJS({
        prepDate: schedule.prepDate,
        pickupDate: schedule.pickupDate,    
      })
    }
    let showDeliveryDate = false;
    if (this.state.deliveryDate !== "" && this.state.pickupDate === this.props.selectedPickupDate) {
      showDeliveryDate = true;
    }
    return (
      <Form
        errors={this.state.errors}
        initialValues={initialValues}
        validate={validateForm}
        message={message}
        deliveryDate={this.state.deliveryDate}
        loadingDeliveryDate={this.state.loadingDeliveryDate}
        showDeliveryDate={showDeliveryDate}
        showDatePicker={this.handleShowDatePicker.bind(this)}
        showDatesAreRecommended={showDatesAreRecommended}
        onSubmit={this.handleSubmit.bind(this)}
        onCancel={this.handleCancel.bind(this)}
        onAutoSetDates={this.handleAutoSetDates.bind(this)}
        onUseCalendar={this.handleUseCalendar.bind(this)}
        />
    )
  }

}

const SHIPMENT_DELIVERY_SCHEDULE = gql`
  query shipmentDeliverySchedule($orderId:ID!, $deliveryDate:String!) {
    shipmentDeliverySchedule(orderId: $orderId, deliveryDate: $deliveryDate) {
      prepDate
      pickupDate
      deliveryDate
    }
  }
`;

const SCHEDULE_SHIPMENT = gql`
  mutation scheduleShipment($data: ScheduleShipmentInput!) {
    scheduleShipment(input: $data) {
      errors { key message }
    }
  }
`;


const withQuery = compose(
  graphql(SHIPMENT_SCHEDULE_WITH_RECOMMENDATION_AND_HOLIDAYS, {
    options: (props) => ({
      fetchPolicy: "network-only",
      variables: {
        orderId: props.orderId
      }
    })
  }),
  graphql(SCHEDULE_SHIPMENT, {
    props: ({ mutate }) => ({
      scheduleShipment: (data) => mutate({
        variables: { data: data }
      })
    })
  })  
);

const selector = formValueSelector("ScheduleShipmentForm");
function mapStateToProps(state, ownProps) {
  return {
    selectedPrepDate: selector(state, "prepDate"),
    selectedPickupDate: selector(state, "pickupDate")
  }
}


export default connect(mapStateToProps, {
  showDatePicker, 
  cancelScheduleShipment, 
  change
})(withApollo(withQuery(ScheduleShipmentFormContainer)));
